import React, { useEffect, useState } from "react";
import { CertificateRegistry } from "../../../Component/Model/Dto/CertificateRegistry/CertificateRegistry";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CertificateRegistryModalSchema } from "./CertificateRegistryValidationSchema";
import { toast } from "react-toastify";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    FormHelperText,
    Grid,
    TextField
} from "@mui/material";
import { CustomDialogTitle } from "../../Common/Custom/CustomDialogTitle";
import { MDBox } from "../../../Component/Common/Components/MDBox";
import { LoadingButton } from "@mui/lab";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { CertificateRegistryService } from "../../../Component/Api/VerificationAgency/CertificateRegistry/CertificateRegistryService";
import AppTextInput from "../../../Component/Common/Components/FormValidationInputs/AppTextInput";
import { AppDatepicker } from "../../../Component/Common/Components/FormValidationInputs/AppDatepicker";
import { MDTypography } from "../../../Component/Common/Components/MDTypography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ScorecardType } from "../../../Component/Model/Dto/StaticData";
import AppCheckbox from "../../../Component/Common/Components/FormValidationInputs/AppCheckbox";
import { SmallLoading } from "../../../Component/Common/Components/Loading/IntermediateLoading";
import { VaCertificateRegistryScorecardSpecificFields } from "../../../Component/Model/Enum/BEE123Enums";
import { PortalDocuments } from "../../Common/PortalDocuments/PortalDocuments";
import { MDButton } from "../../../Component/Common/Components/MDButton";
import { StaticDataService } from "../../../Component/Api/StaticData/StaticDataService";
import { CoreStaticDataService } from "../../../Component/Api/StaticData/CoreStaticDataService";
import { CoreEnumLookupDto } from "../../../Component/Model/Dto/CoreStaticData";

interface IProps {
    id?: number;
    scorecardType: ScorecardType;
    showModal: boolean;
    handleCancel: () => void;
}

export const CertificateRegistryModal = (props: IProps) => {

    const [certificateId, setCertificateId] = useState<number | undefined>(props.id);
    const [scorecardTypesForFieldsMappings, setScorecardTypesForFieldsMappings] = useState<CertificateRegistry.FieldMappingDto[]>([]);
    const [showDocumentsPopUp, setShowDocumentsPopUp] = useState<boolean>(false);
    const [scorecardTypeName, setScorecardTypeName] = useState<string>(props.scorecardType.Name);
    const [scorecardTypeId, setScorecardTypeId] = useState<number>(props.scorecardType.Id);
    const [editDisabled, setEditDisabled] = useState<boolean>(false);
    const [hasSignature, setHasSignature] = useState<boolean>(false);
    const [disableNumberVersion, setDisableNumberVersion] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [showMatchFound, setShowMatchFound] = useState<boolean>(false);
    const [allowedElements, setAllowedElements] = useState<CoreEnumLookupDto[]>([]);
    
    const hasOwnership = allowedElements.filter(x => x.name === "Ownership").length > 0 || scorecardTypeId === 0;

    let scorecardTypes: ScorecardType[] = [];

    const { control, reset, handleSubmit, formState: { isSubmitting }, setValue, watch } = useForm({
        mode: "all",
        resolver: yupResolver(CertificateRegistryModalSchema)
    });

    const documentLink = "CertificateRegistry";
    const measurementPeriodStartDate = watch("measurementPeriodStartDate");
    const measurementPeriodEndDate = watch("measurementPeriodEndDate");
    const issueDate = watch("issueDate");
    const expiryDate = watch("expiryDate");

    const certificateNumber = watch("certificateNumber");
    const versionNumber = watch("versionNumber");

    const getCertificateEntry = async (id: number) => {
        await getScorecardTypes();
        try {
            const result = await CertificateRegistryService.GetCertificateEntryById(id);
            setScorecardTypeName(result.data.scorecardType);
            if (result.data.scorecardId != null ) {
                setEditDisabled(true);
            }
            setHasSignature(result.data.hasSignature == true)
            reset(result.data);

            if (scorecardTypes.length >= 0) {
                const dataScorecardType = result.data.scorecardType.trim()
                const type = scorecardTypes.find((type) => type.Name === dataScorecardType);
                if (type !== undefined) {
                    setScorecardTypeId(type.Id);
                }
            }

        } catch (error) {
            console.log(error)
        }
        finally {
            setLoading(false);
        }
    }

    const handleDataSubmit: SubmitHandler<FieldValues> = async (certificate: FieldValues) => {
        try {
            const certificateDto = certificate as CertificateRegistry.Dto;
            certificateDto.scorecardType = scorecardTypeName;
            if (!certificateId) {
                const result = await CertificateRegistryService.CreateCertificateEntry(certificateDto);
                toast.success("Certificate entry added succesfully");
                const savedEntryId = result.data.id;
                setCertificateId(savedEntryId);
                setValue("id", savedEntryId);
            }

            else {
                const result = await CertificateRegistryService.UpdateCertificateEntry(certificateDto);
                toast.success("Certificate entry updated succesfully");
            }

        } catch (error: any) {
            if (error.response) {
                toast.error(error.response.data);
            }
        }
    }

    const handleCancel = () => {
        props.handleCancel();
    }

    const getScorecardTypesForFieldsMapping = async () => {
        try {
            const result = await CertificateRegistryService.GetScorecardTypesForFieldsMapping();
            setScorecardTypesForFieldsMappings(result.data);

        } catch (error) {
            setScorecardTypesForFieldsMappings([]);
        }
    }

    const getScorecardTypes = async () => {
        try {
            const response = await StaticDataService.getApplicableBEEScorecardTypes();
            if (response.data && response.data.length > 0) {
                scorecardTypes = response.data;
            }
            else {
                scorecardTypes = [];
            }
        } catch (error) {
            console.log(error);
        }
    }


    const displayFieldForScorecardType = (id: VaCertificateRegistryScorecardSpecificFields) => {
        const searchObject = scorecardTypesForFieldsMappings.find((field) => field.fieldId === id);
        if (searchObject == null) {
            return false;
        }
        else {
            return searchObject.scorecardTypes.includes(scorecardTypeId);
        }
    }

    const getScorecardTypeElements = async () => {
        try {
            const result = await CoreStaticDataService.getScorecardAllowedElements(scorecardTypeId);
            setAllowedElements(result.data);
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        getScorecardTypesForFieldsMapping();

        if (certificateId !== undefined) {
            setLoading(true);
            getCertificateEntry(certificateId);
            setDisableNumberVersion(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (scorecardTypeId > 0) {
            getScorecardTypeElements();
        }
    }, [scorecardTypeId])

    const certificateNumberVersionValidate = async () => {
        if (certificateNumber) {
            let request: CertificateRegistry.ValidateDto = {
                certificateNumber,
                versionNumber

            };
            let foundMatch = await CertificateRegistryService.ValidateCertificateNumberVersion(request);
            setShowMatchFound(foundMatch.data);
        }
    }

    return (
        <>
            <Box>
                <Dialog open={props.showModal} onClose={handleCancel} aria-labelledby="form-dialog-title" maxWidth="xl" fullWidth>
                    <CustomDialogTitle id="customized-dialog-title" onClose={handleCancel}>
                        {props.id !== undefined ? "Edit Certificate" : "Add Certificate"}
                    </CustomDialogTitle>
                    <DialogContent>
                        {(!loading && scorecardTypesForFieldsMappings.length > 0) ? <Grid container justifyContent="center" sx={{ my: 2 }}>
                            <Grid item xs={11.5} lg={11.5} xl={11.5}>
                                <Box component="form" id="CertificateForm" onSubmit={handleSubmit(handleDataSubmit)}>
                                    <Accordion defaultExpanded={true}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ fill: "white" }} />}
                                            aria-controls="scorecardDetails"
                                            id="scorecardDetails"
                                            sx={{ backgroundColor: "#4d4f53" }}
                                        >
                                            <MDTypography color="white">Measured Entity Details</MDTypography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ mt: 1 }}>
                                            <MDBox>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <MDBox mb={2}>
                                                            <AppTextInput control={control} name="companyName" label="Company Name" disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppTextInput control={control} name="registrationNumber" label="Registration Number" disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppTextInput control={control} name="vatNumber" label="VAT Number" disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppTextInput control={control} name="bbbeeLevelAchieved" label="B-BBEE Level Achieved" disabled={editDisabled} />
                                                        </MDBox>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <MDBox mb={2}>
                                                            <AppTextInput control={control} name="natureOfBusiness" label="Nature Of Business" disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <TextField label="Scorecard Type" value={scorecardTypeName} fullWidth disabled />
                                                        </MDBox>
                                                        <Box mb={1}>
                                                            <MDTypography sx={{ fontSize: "1rem" }} mb={2}>Measurement Period</MDTypography>
                                                        </Box>
                                                        <Box mb={2}>
                                                            <Grid container spacing={2}>
                                                                <Grid item sm={6} md={6} lg={6}>
                                                                    <AppDatepicker control={control} name="measurementPeriodStartDate" label="Start Date" inputFormat="yyyy/MM/dd" maxDate={measurementPeriodEndDate} disabled={editDisabled} />
                                                                </Grid>
                                                                <Grid item sm={6} md={6} lg={6}>
                                                                    <AppDatepicker control={control} name="measurementPeriodEndDate" label="End Date" inputFormat="yyyy/MM/dd" minDate={measurementPeriodStartDate} disabled={editDisabled} />
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </MDBox>
                                        </AccordionDetails>
                                    </Accordion>

                                    <Accordion defaultExpanded={true}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ fill: "white" }} />}
                                            aria-controls="scorecardDetails"
                                            id="scorecardDetails"
                                            sx={{ backgroundColor: "#4d4f53" }}
                                        >
                                            <MDTypography color="white">Certificate Details</MDTypography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ mt: 1 }}>
                                            <MDBox>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <MDBox mb={2}>
                                                            <AppTextInput control={control} name="certificateNumber" label="Certificate Number" onBlur={certificateNumberVersionValidate} disabled={disableNumberVersion} />
                                                            {showMatchFound &&
                                                                <FormHelperText sx={{ color: 'red' }}>The combination of certificate number and version number must be unique</FormHelperText>
                                                            }
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppTextInput control={control} name="versionNumber" label="Version Number" onBlur={certificateNumberVersionValidate} disabled={disableNumberVersion} />
                                                            {showMatchFound &&
                                                                <FormHelperText sx={{ color: 'red' }}>The combination of certificate number and version number must be unique</FormHelperText>
                                                            }
                                                        </MDBox>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <MDBox mb={2}>
                                                            <AppDatepicker control={control} name="issueDate" label="Issue Date" inputFormat="yyyy/MM/dd" maxDate={expiryDate} disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppDatepicker control={control} name="expiryDate" label="Expiry Date" inputFormat="yyyy/MM/dd" minDate={issueDate} disabled={editDisabled} />
                                                        </MDBox>
                                                    </Grid>
                                                </Grid>
                                            </MDBox>
                                        </AccordionDetails>
                                    </Accordion>

                                    <Accordion defaultExpanded={true}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ fill: "white" }} />}
                                            aria-controls="scorecardDetails"
                                            id="scorecardDetails"
                                            sx={{ backgroundColor: "#4d4f53" }}
                                        >
                                            <MDTypography color="white">Element Details</MDTypography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ mt: 1 }}>
                                            <MDBox>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <MDBox mb={2}>
                                                            <AppTextInput control={control} name="bbbeeRecognitionPercentage" label="B-BBEE Recognition Percentage" disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppTextInput control={control} name="sectorCodes" label="Sector Codes" disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppTextInput control={control} name="subSectorCodes" label="Subsector Codes" disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppTextInput control={control} name="totalPoints" label="Total Points Achieved Score" disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppTextInput control={control} name="procurementRecognitionLevel" label="Procurement Recognition Level" disabled={editDisabled} />
                                                        </MDBox>
                                                        {(scorecardTypeId === 0 || (displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Ownership) && hasOwnership)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="equityOwnershipAchievedScore" label="Ownership Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Management_Control)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="managementControlAchievedScore" label="Management Control Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Skills_Development)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="skillsDevelopmentAchievedScore" label="Skills Development Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }

                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Enterprise_Development)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="enterpriseDevelopmentAchievedScore" label="Enterprise Development Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Socio_Economic_Development)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="socioEconomicDevelopmentAchievedScore" label="Socio-Economic Development Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Access_to_Financial_Services)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="accessToFinancialServicesAchievedScore" label="Access to Financial Services Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                        <MDBox mb={2}>
                                                            <AppTextInput control={control} name="empowermentFinancingAndEnterpriseAndSupplierDevelopmentAchievedScore" label="Empowerment Financing & Enterprise & Supplier Development Achieved Score" disabled={editDisabled} />
                                                        </MDBox>
                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Preferential_Procurement)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="preferentialProcurementAchievedScore" label="Preferential Procurement Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Preferential_Procurement_and_Supplier_Development)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="preferentialProcurementAndSupplierDevelopmentAchievedScore" label="Preferential Procurement & Supplier Development Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Empowerment_Financing_and_ESD)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="empowermentFinancingAndESDAchievedScore" label="Empowerment Financing & ESD Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Enterprise_and_Supplier_Development)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="enterpriseAndSupplierDevelopmentAchievedScore" label="Enterprise & Supplier Development Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Supplier_Development)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="supplierDevelopmentAchievedScore" label="Supplier Development Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Economic_Development)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="economicDevelopmentAchievedScore" label="Economic Development Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Employment_Equity)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="employmentEquityAchievedScore" label="Employment Equity Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.RSM_Contributions)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="rsmAchievedScore" label="RSM Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                        {(scorecardTypeId === 0 || displayFieldForScorecardType(VaCertificateRegistryScorecardSpecificFields.Localisation)) &&
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="localisationAchievedScore" label="Localisation Achieved Score" disabled={editDisabled} />
                                                            </MDBox>
                                                        }
                                                        <MDBox mb={2}>
                                                            <AppCheckbox control={control} name="discountingPrincipalApplied" label="Discounting Principal Applied" disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppCheckbox control={control} name="modifiedFlowThroughPrincipleApplied" label="Modified Flow Through Principle Applied" disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppCheckbox control={control} name="yesInitiative" label="Y.E.S Initiative" disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppCheckbox control={control} name="exclusionPrincipleApplied" label="Exclusion Principle Applied" disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppCheckbox control={control} name="empoweringSupplier" label="Empowering Supplier" disabled={editDisabled} />
                                                        </MDBox>
                                                        <MDBox mb={2}>
                                                            <AppCheckbox control={control} name="priorityElementsAchieved" label="Priority Elements Achieved" disabled={editDisabled} />
                                                        </MDBox>
                                                    </Grid>
                                                </Grid>
                                            </MDBox>
                                        </AccordionDetails>
                                    </Accordion>

                                    {hasOwnership &&
                                        <Accordion defaultExpanded={true}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon sx={{ fill: "white" }} />}
                                                aria-controls="ownershipDetails"
                                                id="ownershipDetails"
                                                sx={{ backgroundColor: "#4d4f53" }}
                                            >
                                                <MDTypography color="white">Ownership Details</MDTypography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ mt: 1 }}>
                                                <MDBox>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="percentBlackOwnershipVotingRights" label="% Black Ownership Voting Rights" disabled={editDisabled} />
                                                            </MDBox>
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="percentBlackOwnershipEconomicInterest" label="% Black Ownership Economic Interest" disabled={editDisabled} />
                                                            </MDBox>
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="percentBlackWomenOwnershipVotingRights" label="% Black Women Ownership Voting Rights" disabled={editDisabled} />
                                                            </MDBox>
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="percentBlackWomenOwnershipEconomicInterest" label="% Black Women Ownership Economic Interest" disabled={editDisabled} />
                                                            </MDBox>
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="percentBlackDesignatedGroups" label="% Black Designated Groups" disabled={editDisabled} />
                                                            </MDBox>
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="blackYouthShareholdingPercentage" label="% Black Youth" disabled={editDisabled} />
                                                            </MDBox>
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="blackDisabledShareholdingPercentage" label="% Black Disabled" disabled={editDisabled} />
                                                            </MDBox>
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="blackUnemployedShareholdingPercentage" label="% Black Unemployed" disabled={editDisabled} />
                                                            </MDBox>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="blackPeopleLivingInRuralAreasShareholdingPercentage" label="% Black People Living in Rural Areas" disabled={editDisabled} />
                                                            </MDBox>
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="blackMilitaryVeteransShareholdingPercentage" label="% Black Military Veterans" disabled={editDisabled} />
                                                            </MDBox>
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="blackNewEntrants" label="% Black New Entrants" disabled={editDisabled} />
                                                            </MDBox>
                                                            <MDBox mb={2}>
                                                                <AppTextInput control={control} name="blackOwnershipTotal" label="Black Ownership Total" disabled={editDisabled} />
                                                            </MDBox>
                                                            <MDBox mb={2}>
                                                                <AppCheckbox control={control} name="achieve15xYESTargetand5Absorption" label="Achieve 1.5x Y.E.S Target and 5% Absorption" disabled={editDisabled} />
                                                            </MDBox>
                                                            <MDBox mb={2}>
                                                                <AppCheckbox control={control} name="achieveDoubleYESTargetand5Absorption" label="Achieve Double Y.E.S Target and 5% Absorption" disabled={editDisabled} />
                                                            </MDBox>
                                                            <MDBox mb={2}>
                                                                <AppCheckbox control={control} name="achieveYESTargetand25Absorption" label="Achieve Y.E.S Target and 2.5% Absorption" disabled={editDisabled} />
                                                            </MDBox>
                                                        </Grid>
                                                    </Grid>
                                                </MDBox>
                                            </AccordionDetails>
                                        </Accordion>
                                    }
                                </Box>
                            </Grid>
                        </Grid>
                            : <SmallLoading />}
                    </DialogContent>
                    <DialogActions>
                        {(certificateId != null && (!editDisabled || !hasSignature)) &&
                            <MDBox >
                                <MDButton variant="contained" color="primary" onClick={() => setShowDocumentsPopUp(true)} startIcon={<FileUploadIcon />}>
                                    Upload Certificate File
                                </MDButton>
                            </MDBox>
                        }
                        <MDBox>
                            <LoadingButton variant="contained" color="primary" type="submit" form="CertificateForm" loading={isSubmitting} disabled={editDisabled}>
                                Save
                            </LoadingButton>

                        </MDBox>
                    </DialogActions>
                </Dialog>
            </Box>
            {(showDocumentsPopUp && certificateId != null) &&
                <PortalDocuments display={showDocumentsPopUp} link={documentLink} id={certificateId} handleCancel={() => setShowDocumentsPopUp(false)} />
            }
        </>
    );
}
